import React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import PublicNavbar from '../../components/public/PublicNavbar';
import { PublicFooter } from '../../components/public/landingSections';
import { focusRing } from '../../components/public/landingStyles';
import {
  formatNeighborhoodValue,
  lookupNeighborhood,
  NeighborhoodReport,
} from '../../services/neighborhood';

const NeighborhoodPage: React.FC = () => {
  const [zip, setZip] = React.useState('');
  const [report, setReport] = React.useState<NeighborhoodReport | null>(null);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const lookup = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setBusy(true);
    setError('');
    setReport(null);
    try {
      setReport(await lookupNeighborhood(zip.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Neighborhood figures are unavailable right now.');
    } finally {
      setBusy(false);
    }
  };

  const published = report?.metrics.filter((metric) => metric.value != null) ?? [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicNavbar />
      <Box
        id="main"
        component="main"
        tabIndex={-1}
        sx={{ px: { xs: 2, sm: 3 }, py: { xs: 4, md: 6 }, maxWidth: 800, mx: 'auto' }}
      >
        <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '2rem', md: '2.5rem' } }}>
          Neighborhood figures
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
          Median income, rent, home value, population, and vacancy come from the US Census Bureau
          American Community Survey 5-year estimates for a ZIP code tabulation area. These are not a
          listing, not an appraisal, and not a recommendation to purchase.
        </Typography>
        <Box component="form" onSubmit={lookup} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          <TextField
            label="ZIP code"
            value={zip}
            onChange={(event) => setZip(event.target.value)}
            inputProps={{ inputMode: 'numeric', 'aria-label': 'ZIP code', maxLength: 5 }}
            sx={{ minWidth: 160 }}
          />
          <Button type="submit" variant="contained" disabled={busy} sx={{ minHeight: 44, ...focusRing }}>
            {busy ? 'Looking up…' : 'Look up'}
          </Button>
        </Box>
        {error && (
          <Typography role="alert" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {!report && !error && (
          <Typography color="text.secondary">Enter a 5-digit ZIP code to load published Census figures.</Typography>
        )}
        {report && (
          <Box component="section" aria-labelledby="neighborhood-result">
            <Typography id="neighborhood-result" variant="h5" component="h2" sx={{ fontWeight: 800, mb: 1 }}>
              {report.name || `ZIP ${report.zip}`}
            </Typography>
            {published.length === 0 ? (
              <Typography sx={{ mb: 2 }}>
                {report.message || 'The Census Bureau did not publish these figures for that ZIP code.'}
              </Typography>
            ) : (
              <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, m: 0 }}>
                {report.metrics.map((metric) => (
                  <Box key={metric.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Typography component="dt" variant="body2" color="text.secondary">
                      {metric.label}
                    </Typography>
                    <Typography component="dd" variant="h6" sx={{ m: 0, fontWeight: 800 }}>
                      {formatNeighborhoodValue(metric)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Source: {report.source}
              {report.year ? `, ${report.year} 5-year estimates` : ''}.{' '}
              <Box component="a" href={report.sourceUrl} sx={{ color: 'text.primary', fontWeight: 700 }}>
                census.gov
              </Box>
              . Figures are cached for about a day. A blank metric means Census did not publish it.
            </Typography>
          </Box>
        )}
        <PublicFooter />
      </Box>
    </Box>
  );
};

export default NeighborhoodPage;
